import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { search as searchUrl } from 'ggApp/utils/urlBuilder/frontshop';
import ScrollLock, { TouchScrollable } from 'react-scrolllock-configurable';
import CloseIcon from 'ggApp/shared/icons/close';
import { withTheme } from 'styled-components';
import { sanitize } from 'dompurify';
import { sendAnalyticsEvent } from '@devsbb/analytics-client';

import { SEARCH_OVERLAY_ACTION } from 'ggApp/modules/gaAnalytics/constants';
import { SegmentEvents } from '../../../../../../packages/analytics/events';
import {
    OuterWrap,
    InnerWrap,
    Form,
    Input,
    PlaceholderText,
    CloseButton,
    SearchContent,
} from './styles';

class Bar extends Component {
    fetchProducts = debounce(
        // eslint-disable-next-line react/destructuring-assignment
        (query) => this.props.fetchProductsForSearch(query),
        400,
        {
            leading: false,
            trailing: true,
        },
    );

    constructor(props) {
        super(props);
        this.state = {
            query: '',
            searchIsActive: false,
            activeRandom: false,
        };
    }

    componentDidMount() {
        window.addEventListener('keydown', ::this.onKey);
        const { filters, fetchDefaultProductsForSearch, locationPathname } = this.props;

        if (locationPathname.includes('your-')) {
            this.setState({ activeRandom: true });
        }
        window.addEventListener('load', () => this.setState({ activeRandom: true }));

        fetchDefaultProductsForSearch();
        if (Object.keys(filters).includes('search')) {
            this.setQuery(filters.search);
            this.fetchProducts(filters.search);
        }
    }

    componentWillReceiveProps(nextProps) {
        const { locationPathname: currentLocation } = this.props;
        const { locationPathname: nextLocation } = nextProps;
        if (currentLocation !== nextLocation) {
            this.setState({ searchIsActive: false });
            if (!/search/.test(nextLocation)) {
                this.setQuery('');
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', ::this.onKey);
    }

    onKey(e) {
        const { searchIsActive } = this.state;
        const { registerSearchOverlayEvent } = this.props;
        if (e.keyCode === 9 && searchIsActive) {
            // disable tab
            e.preventDefault();
            return;
        }
        if (e.keyCode === 13 && searchIsActive) {
            registerSearchOverlayEvent(SEARCH_OVERLAY_ACTION.pressEnter);
        }
        const { closeOverlay } = this.props;
        if (e.keyCode === 27 && searchIsActive) closeOverlay(); // escape key
    }

    onBlurSearch = () => {
        this.setState({ searchIsActive: false });
    };

    setQuery = (query) => {
        const sanitized = sanitize(query.replace(/[<>/]/g, ''));
        this.setState({ query: sanitized });
        this.fetchProducts(sanitized);
    };

    openOverlay = () => {
        const { openOverlay, registerSearchOverlayEvent, overlayIsOpen, storeActive } = this.props;
        if (overlayIsOpen) return;
        sendAnalyticsEvent(SegmentEvents.searchEntered, {
            store_id: storeActive.store_id,
        });
        openOverlay('search');

        registerSearchOverlayEvent(SEARCH_OVERLAY_ACTION.enter);

        this.setState({ searchIsActive: true });
    };

    onCtaClickWithGtmEvent = () => {
        const { registerSearchOverlayEvent, storeActive } = this.props;
        const { query } = this.state;
        registerSearchOverlayEvent(SEARCH_OVERLAY_ACTION.ctaClick);
        sendAnalyticsEvent(SegmentEvents.searchCTAClicked, {
            store_id: storeActive.store_id,
            search_term: query,
        });
    };

    resetOverlay = (e) => {
        if (e) {
            e.stopPropagation();
        }
        const { closeOverlay } = this.props;
        this.setQuery('');
        this.searchContent.scrollTop = 0;

        closeOverlay();
    };

    closeOverlayWithGtmEvent = (event, shouldReset = false) => {
        const { registerSearchOverlayEvent, closeOverlay, storeActive } = this.props;
        const { query } = this.state;
        registerSearchOverlayEvent(SEARCH_OVERLAY_ACTION.exit);
        sendAnalyticsEvent(SegmentEvents.searchInputExited, {
            store_id: storeActive.store_id,
            search_term: query,
        });
        if (shouldReset) {
            this.resetOverlay(event);
            return;
        }

        closeOverlay();
    };

    removeFocus = () => {
        // removes focus from input, thus hiding virtual keyboard.
        if (document) {
            setTimeout(() => {
                document.activeElement.blur();
            }, 500);
        }
    };

    handleChange = (e) => {
        this.setQuery(e.target.value);
    };

    handleRedirect = (e) => {
        e.preventDefault();
        const { mainTransition, storeActive } = this.props;
        const { query } = this.state;
        sendAnalyticsEvent(SegmentEvents.searchTermUsed, {
            store_id: storeActive.store_id,
            search_term: query,
        });
        if (window.ga) {
            window.ga('send', 'pageview', `/search?filter=search%3D${query}`);
        }
        if (query) {
            mainTransition(searchUrl(query), true);
        }
    };

    handleClickOutside() {
        const { overlayIsOpen } = this.props;
        if (overlayIsOpen) {
            this.closeOverlayWithGtmEvent(null);
        }
    }

    render() {
        const { className, overlayIsOpen, isLoading, mainTransition, theme } = this.props;
        const { query, activeRandom } = this.state;
        return (
            <OuterWrap className={className} onClick={this.openOverlay}>
                <InnerWrap overlayIsOpen={overlayIsOpen}>
                    <Form onSubmit={this.handleRedirect} overlayIsOpen={overlayIsOpen}>
                        <Input
                            type="text"
                            autoComplete="false"
                            onChange={this.handleChange}
                            value={query}
                            tabIndex="-1"
                            onBlur={this.onBlurSearch}
                        />

                        {!query && activeRandom && <PlaceholderText />}

                        <CloseButton
                            onClick={(e) => this.closeOverlayWithGtmEvent(e, true)}
                            visible={overlayIsOpen}
                        >
                            <CloseIcon color={theme.colors.Foreground} />
                        </CloseButton>
                    </Form>

                    {overlayIsOpen && <ScrollLock />}

                    <TouchScrollable>
                        <SearchContent
                            isLoading={isLoading}
                            query={query}
                            mainTransition={mainTransition}
                            handleTouchStart={this.removeFocus}
                            closeOverlay={this.resetOverlay}
                            contentRef={(el) => {
                                this.searchContent = el;
                            }}
                            visible={overlayIsOpen}
                            onClickCta={this.onCtaClickWithGtmEvent}
                        />
                    </TouchScrollable>
                </InnerWrap>
            </OuterWrap>
        );
    }
}

Bar.propTypes = {
    className: PropTypes.string,
    locationPathname: PropTypes.string,
    openOverlay: PropTypes.func,
    closeOverlay: PropTypes.func,
    overlayIsOpen: PropTypes.bool,
    fetchProductsForSearch: PropTypes.func,
    mainTransition: PropTypes.func,
    isLoading: PropTypes.bool,
    filters: PropTypes.shape({
        search: PropTypes.string,
    }),
    registerSearchOverlayEvent: PropTypes.func,
    theme: PropTypes.shape({
        colors: PropTypes.object,
    }).isRequired,
};

Bar.defaultProps = {
    className: '',
    locationPathname: '',
    openOverlay: () => {},
    closeOverlay: () => {},
    overlayIsOpen: false,
    fetchProductsForSearch: () => {},
    mainTransition: () => {},
    filters: [],
    isLoading: false,
    registerSearchOverlayEvent: () => {},
};

export default withTheme(Bar);
